// components/CameraPopup.jsx — V2 Corporate sobre
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, X, RotateCcw, Check, AlertCircle, Loader2 } from 'lucide-react';

const CameraPopup = ({ isOpen, onClose, onCapture, employeeName }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [cameraReady, setCameraReady] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [error, setError] = useState(null);

  const startCamera = useCallback(async () => {
    try {
      setError(null);
      setCameraReady(false);
      let stream;
      try {
        // Essai avec caméra frontale
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false
        });
      } catch (e) {
        // Fallback sans contrainte facingMode
        stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false
        });
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setCameraReady(true);
        };
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError("Accès à la caméra refusé. Autorisez l'accès dans les paramètres du navigateur.");
      } else if (err.name === 'NotFoundError') {
        setError('Aucune caméra détectée sur cet appareil.');
      } else {
        setError("Impossible d'accéder à la caméra. Vérifiez vos permissions.");
      }
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        track.enabled = false;
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraReady(false);
  }, []);

  useEffect(() => {
    console.log('🔍 [DIAG] CameraPopup useEffect - isOpen:', isOpen);
    if (isOpen) {
      setCapturedPhoto(null);
      setError(null);
      startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [isOpen, startCamera, stopCamera]);

  // Capture instantanée — sans countdown
  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !cameraReady) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    setCapturedPhoto(canvas.toDataURL('image/jpeg', 0.85));
    stopCamera();
  };

  const handleRetake = () => {
    setCapturedPhoto(null);
    startCamera();
  };

  const handleValidate = () => {
    if (capturedPhoto) {
      fetch(capturedPhoto).then(r => r.blob()).then(blob => onCapture(blob));
    }
  };

  const handleClose = () => {
    // Forcer l'arrêt complet de tous les tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        track.enabled = false;
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCapturedPhoto(null);
    setCameraReady(false);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-white w-full mx-4 overflow-hidden"
        style={{ maxWidth: 440, borderRadius: 20, boxShadow: '0 24px 60px rgba(0,0,0,0.2)' }}>

        {/* HEADER */}
        <div className="relative flex items-center justify-between px-6 py-4"
          style={{ backgroundColor: '#448696' }}>
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: 3,
            background: 'linear-gradient(90deg, #448696, #fed774, #fdac84)'
          }} />
          <div className="flex items-center gap-3">
            <Camera size={20} className="text-white" />
            <div>
              <p className="text-sm font-bold text-white">Vérification d'identité</p>
              <p className="text-xs text-white/70">Photo de pointage sécurisé</p>
            </div>
          </div>
          <button onClick={handleClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors text-white">
            <X size={16} />
          </button>
        </div>

        {/* NOM EMPLOYÉ */}
        <div className="px-6 py-3 border-b border-gray-100" style={{ backgroundColor: '#f8fafb' }}>
          <p className="text-xs text-gray-500 mb-0.5">Pointage de</p>
          <p className="text-sm font-bold text-gray-900">{employeeName || 'Employé'}</p>
        </div>

        {/* ZONE CAMÉRA */}
        <div className="relative bg-gray-900" style={{ minHeight: 300 }}>

          {/* Erreur */}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center p-8">
              <div className="text-center">
                <AlertCircle size={36} className="mx-auto mb-3 text-red-400" />
                <p className="text-white/80 text-sm mb-4 leading-relaxed">{error}</p>
                <button onClick={startCamera}
                  className="px-5 py-2 rounded-xl text-sm font-semibold text-white"
                  style={{ backgroundColor: '#448696' }}>
                  Réessayer
                </button>
              </div>
            </div>
          )}

          {/* Loading */}
          {!cameraReady && !error && !capturedPhoto && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <Loader2 size={28} className="mx-auto mb-2 text-white/50 animate-spin" />
                <p className="text-white/40 text-sm">Activation de la caméra...</p>
              </div>
            </div>
          )}

          {/* Vidéo */}
          <video ref={videoRef} autoPlay playsInline muted
            style={{
              width: '100%',
              display: capturedPhoto ? 'none' : 'block',
              transform: 'scaleX(-1)',
              opacity: cameraReady ? 1 : 0,
              transition: 'opacity 0.3s ease'
            }}
          />

          {/* Guide ovale sobre */}
          {cameraReady && !capturedPhoto && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div style={{
                width: 160, height: 200, borderRadius: '50%',
                border: '2px dashed rgba(255,255,255,0.35)'
              }} />
              <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                <span className="text-xs text-white/60 font-medium px-3 py-1 rounded-lg"
                  style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
                  Placez votre visage dans le cadre
                </span>
              </div>
            </div>
          )}

          {/* Photo capturée */}
          {capturedPhoto && (
            <img src={capturedPhoto} alt="Photo" style={{ width: '100%', display: 'block' }} />
          )}

          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>

        {/* BOUTONS */}
        <div className="p-5 space-y-3" style={{ backgroundColor: '#fdfcf5' }}>
          {!capturedPhoto ? (
            <button onClick={handleCapture}
              disabled={!cameraReady}
              className="w-full py-3.5 text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#448696' }}>
              <Camera size={18} />
              Prendre la photo
            </button>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <button onClick={handleRetake}
                className="py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 border border-gray-200 hover:bg-gray-100 transition-colors text-gray-700 bg-white">
                <RotateCcw size={15} />
                Reprendre
              </button>
              <button onClick={handleValidate}
                className="py-3 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90"
                style={{ backgroundColor: '#448696' }}>
                <Check size={15} />
                Valider
              </button>
            </div>
          )}

          <button onClick={handleClose}
            className="w-full py-2 text-xs text-gray-400 hover:text-gray-600 transition-colors">
            Annuler le pointage
          </button>
        </div>

      </div>
    </div>
  );
};

export default CameraPopup;